import { useMemo } from "react";
import { Tooltip } from "antd";
import { timetableCourseColors } from "./timetableColors.js";
import { getSubjectName } from "../../utils/academy.js";
import {
  buildAcademyTimetable,
  TIMETABLE_GROUPS,
  timetableLanes,
} from "./academyTimetable.js";

const grade = (course) =>
  course.schoolLevel === "재수"
    ? "재수"
    : `${({ 초등: "초", 중등: "중", 고등: "고" })[course.schoolLevel] || ""}${
      course.grade || ""
    }`;
const HOUR_HEIGHT = 40;
const name = (course, subjects) => {
  const title = [grade(course), course.className]
    .filter((value) => value && value !== "-").join(" ");
  const subject = getSubjectName(subjects, course.subjectId, course.subject).trim();
  return [title, subject && subject !== "-" && subject !== "수학" ? `(${subject})` : ""]
    .filter(Boolean).join(" ");
};
const clock = (minutes) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${
    String(minutes % 60).padStart(2, "0")
  }`;

export function AcademyWeeklyGrid({ courses, subjects, teachers, colorCourses = courses }) {
  const model = useMemo(() => buildAcademyTimetable(courses, teachers), [
    courses,
    teachers,
  ]);
  const colors = useMemo(() => timetableCourseColors(colorCourses), [colorCourses]);
  const hours = Array.from(
    { length: (model.end - model.start) / 60 },
    (_, i) => model.start + i * 60,
  );
  const bodyHeight = hours.length * HOUR_HEIGHT;
  const columnCount = Math.max(model.teachers.length, 1);
  return (
    <div className="academy-weekly-workspace">
      <div
        className="academy-weekly-scroll"
        tabIndex={0}
        aria-label="요일 묶음·선생님별 시간표"
      >
        <div
          className="academy-weekly-grid"
          style={{
            "--academy-weekly-hour-height": `${HOUR_HEIGHT}px`,
            gridTemplateColumns: "76px repeat(4, minmax(0, 1fr))",
          }}
        >
          <div className="academy-weekly-axis">
            <div className="academy-weekly-time-title">시간</div>
            {hours.map((hour) => (
              <div className="academy-weekly-hour" key={hour}>
                {clock(hour)}
                <br />~{clock(hour + 60)}
              </div>
            ))}
          </div>
          {TIMETABLE_GROUPS.map((group) => (
            <section
              key={group.id}
              className="academy-weekly-group"
              aria-label={`${group.label} 시간표`}
            >
              <div className="academy-weekly-group-title">{group.label}</div>
              <div
                className="academy-weekly-teachers"
                style={{
                  gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                }}
              >
                {model.teachers.map((teacher) => (
                  <div
                    className="academy-weekly-teacher"
                    key={teacher.id}
                    data-teacher-id={teacher.id}
                  >
                    <div
                      className="academy-weekly-teacher-title"
                      title={teacher.name}
                    >
                      {teacher.name}
                    </div>
                    <div
                      className="academy-weekly-column"
                      style={{ height: bodyHeight }}
                    >
                      {hours.map((hour) => (
                        <div className="academy-weekly-empty-hour" key={hour} />
                      ))}
                      {timetableLanes(model.blocks.filter((b) =>
                        b.groupId === group.id && b.teacherKey === teacher.id
                      )).map((block) => {
                        const label = `${name(block.course, subjects)}${
                          block.kind === "clinic" ? " 클리닉" : ""
                        }`;
                        const detail =
                          `${group.label} · ${teacher.name} · ${block.startsAt}~${block.endsAt} · ${label}`;
                        const height = (block.end - block.start) / 60 * HOUR_HEIGHT;
                        return (
                          <Tooltip
                            key={block.id}
                            title={`${block.startsAt}~${block.endsAt}`}
                            trigger={["hover", "focus"]}
                          >
                            <div
                              tabIndex={0}
                              role="group"
                              aria-label={detail}
                              data-kind={block.kind}
                              data-course-id={block.course.id}
                              className={`academy-weekly-block${
                                block.kind === "clinic" ? " is-clinic" : ""
                              }${height < 40 ? " is-short" : ""}`}
                              style={{
                                top: (block.start - model.start) / 60 * HOUR_HEIGHT,
                                height,
                                left: `${block.lane / block.laneCount * 100}%`,
                                width: `${100 / block.laneCount}%`,
                                background: block.kind === "clinic" ? "#ffffff" : colors.get(String(block.course.id)),
                              }}
                            >
                              <span className="academy-weekly-block-name">
                                {name(block.course, subjects)}
                              </span>
                              {block.kind === "clinic" && (
                                <span className="academy-weekly-clinic-label">
                                  클리닉
                                </span>
                              )}
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
      {model.unplaced.length > 0 && (
        <div className="timetable-unplaced-hint">시간표에 표시하지 못한 일정 {model.unplaced.length}건</div>
      )}
    </div>
  );
}
